import React, { useState, useEffect } from 'react';
import { signUp, confirmSignUp, signIn, getCurrentUser, signOut } from 'aws-amplify/auth';
import CustomSignUpForm from './CustomSignUpForm';
import { clearAuthCache } from '../utils/authUtils';

interface CustomAuthenticatorProps {
  children: (props: { signOut: () => void; user: any }) => React.ReactNode;
  onWeddingGroupCreate?: (familyId: string, familyName?: string) => Promise<void>;
  checkFamilyIdAvailability?: (familyId: string) => Promise<boolean>;
}

type AuthState = 'signIn' | 'signUp' | 'confirmSignUp' | 'loading';

export const CustomAuthenticator: React.FC<CustomAuthenticatorProps> = ({ 
  children, 
  onWeddingGroupCreate,
  checkFamilyIdAvailability
}) => {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationData, setConfirmationData] = useState<{
    email: string;
    password: string;
    familyId: string;
    familyName: string;
  } | null>(null);

  // 初回ロード時に現在のユーザーをチェック
  useEffect(() => {
    checkCurrentUser();
  }, []);

  const checkCurrentUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      console.log('Current user:', currentUser);
      setUser(currentUser);
      setAuthState('signIn'); // 認証済み状態
    } catch (error) {
      console.log('No authenticated user');
      setUser(null);
      setAuthState('signIn'); // ログインフォーム表示
    }
  };

  // メールアドレス重複チェック機能（削除）
  // AWS Cognitoのサインアップ時に自動的にチェックされるため、事前チェックは不要

  // サインイン処理
  const handleSignIn = async (email: string, password: string) => {
    setIsLoading(true);
    setError('');
    console.log('🔄 CustomAuthenticator: サインイン処理開始 - Email:', email);
    
    try {
      console.log('🔄 CustomAuthenticator: Amplify signIn 呼び出し中...');
      const { isSignedIn } = await signIn({ username: email, password });
      console.log('✅ CustomAuthenticator: Amplify signIn 完了 - isSignedIn:', isSignedIn);
      
      if (isSignedIn) {
        console.log('🔄 CustomAuthenticator: getCurrentUser 呼び出し中...');
        // サインイン成功後、ユーザー情報を設定
        const currentUser = await getCurrentUser();
        console.log('✅ CustomAuthenticator: getCurrentUser 完了 - User:', currentUser);
        console.log('📝 CustomAuthenticator: User attributes:', currentUser?.signInDetails?.loginId);
        
        setUser(currentUser);
        console.log('✅ CustomAuthenticator: ユーザー状態設定完了');
        
        // 認証トークンが正しく取得されているか確認
        try {
          console.log('🔄 CustomAuthenticator: 認証トークン確認中...');
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          console.log('✅ CustomAuthenticator: 認証セッション取得完了');
          console.log('📝 CustomAuthenticator: Access Token 存在:', !!session.tokens?.accessToken);
          console.log('📝 CustomAuthenticator: ID Token 存在:', !!session.tokens?.idToken);
          
          if (session.tokens?.idToken) {
            const tokenPayload = JSON.parse(atob(session.tokens.idToken.toString().split('.')[1]));
            console.log('📝 CustomAuthenticator: ID Token expiry:', new Date(tokenPayload.exp * 1000));
            console.log('📝 CustomAuthenticator: ID Token username:', tokenPayload.username);
            console.log('📝 CustomAuthenticator: ID Token cognito:groups:', tokenPayload['cognito:groups']);
          }
        } catch (tokenError) {
          console.error('❌ CustomAuthenticator: 認証トークン確認エラー:', tokenError);
        }
      }
    } catch (error: any) {
      console.error('❌ CustomAuthenticator: サインインエラー:', error);
      console.error('❌ CustomAuthenticator: エラーメッセージ:', error.message);
      console.error('❌ CustomAuthenticator: エラータイプ:', error.name);
      
      if (error.message.includes('UserNotFoundException')) {
        setError('このメールアドレスは登録されていません。新規アカウント作成をお試しください。');
      } else if (error.message.includes('NotAuthorizedException')) {
        setError('メールアドレスまたはパスワードが正しくありません。');
      } else {
      setError(error.message || 'ログインに失敗しました');
      }
    } finally {
      setIsLoading(false);
      console.log('🏁 CustomAuthenticator: サインイン処理完了');
    }
  };

  // サインアップ処理
  const handleSignUp = async (email: string, password: string, familyId: string, familyName?: string) => {
    setIsLoading(true);
    setError('');
    try {
      const { nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email: email,
          },
        },
      });

      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setConfirmationData({ email, password, familyId, familyName: familyName || familyId });
        setAuthState('confirmSignUp');
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      if (error.message.includes('UsernameExistsException')) {
        setError('このメールアドレスは既に登録されています。ログインをお試しください。');
      } else if (error.message.includes('InvalidPasswordException')) {
        setError('パスワードの形式が正しくありません。8文字以上で大文字・小文字・数字を含めてください。');
      } else {
      setError(error.message || 'アカウント作成に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 確認コード検証
  const handleConfirmSignUp = async (email: string, confirmationCode: string) => {
    setIsLoading(true);
    setError('');
    try {
      const { isSignUpComplete } = await confirmSignUp({
        username: email,
        confirmationCode,
      });

      if (isSignUpComplete && confirmationData) {
        console.log('✅ CustomAuthenticator: サインアップ確認完了 - 自動ログイン開始');
        
        // 確認完了後、自動ログインを実行
        const { isSignedIn } = await signIn({ 
          username: confirmationData.email, 
          password: confirmationData.password 
        });
        
        if (isSignedIn) {
          console.log('✅ CustomAuthenticator: 自動ログイン成功 - ユーザー情報取得中');
          
          // ユーザー情報を設定（トークン取得完了を保証）
          const currentUser = await getCurrentUser();
          setUser(currentUser);
          
          console.log('✅ CustomAuthenticator: ユーザー情報設定完了 - ファミリーアカウント作成開始');
          console.log('FamilyID:', confirmationData.familyId, 'FamilyName:', confirmationData.familyName);
          
          // ファミリーアカウント作成を実行
          try {
            await onWeddingGroupCreate?.(confirmationData.familyId, confirmationData.familyName);
            console.log('✅ CustomAuthenticator: ファミリーアカウント作成成功');
            setConfirmationData(null);
          } catch (familyError: any) {
            console.error('❌ CustomAuthenticator: ファミリーアカウント作成失敗:', familyError);
            setError(`ファミリーアカウントの作成に失敗しました: ${familyError.message || familyError}`);
          }
        } else {
          console.error('❌ CustomAuthenticator: 自動ログイン失敗');
          setError('自動ログインに失敗しました。手動でログインしてください。');
        }
      }
    } catch (error: any) {
      console.error('❌ CustomAuthenticator: 確認コード検証エラー:', error);
      setError(error.message || '確認コードの検証に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // サインアウト処理
  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setAuthState('signIn');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // ローディング中
  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600">認証状態を確認中...</p>
        </div>
      </div>
    );
  }

  // 認証済みの場合、子コンポーネントをレンダリング
  if (user) {
    return (
      <>
        {children({ signOut: handleSignOut, user })}
      </>
    );
  }

  // サインインフォーム
  const SignInForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // URLパラメータからアカウント削除理由を取得
    const urlParams = new URLSearchParams(window.location.search);
    const reason = urlParams.get('reason');
    const isAccountDeleted = reason === 'account_deleted';

    const onSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSignIn(email, password);
    };

    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-pink-600 mb-2">🎊 Wedding Task Planner</h1>
            <p className="text-gray-600">あなたの特別な日を完璧に準備しましょう</p>
            <p className="text-xs text-gray-500 mt-1">登録されたメールアドレスでログインしてください</p>
          </div>

          {/* アカウント削除通知 */}
          {isAccountDeleted && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-red-500 text-lg">⚠️</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    アカウントが削除されました
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      あなたのアカウントはファミリーアカウントのオーナーによって削除されました。
                      再度参加するには、新しい招待リンクが必要です。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                メールアドレス（ユーザー名）
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="登録済みのメールアドレス"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                パスワード
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-pink-600 hover:bg-pink-700 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              {isLoading ? 'ログイン中...' : 'ログイン'}
            </button>

            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={() => {
                  setError('');
                  setAuthState('signUp');
                }}
                className="text-pink-600 hover:text-pink-800 text-sm block w-full"
              >
                新規アカウント作成
              </button>
              
              {/* 緊急用キャッシュクリアボタン */}
              <button
                type="button"
                onClick={async () => {
                  if (window.confirm('認証キャッシュをクリアして、ページをリロードします。よろしいですか？\n\n※ CloudFormation削除後の認証問題を解決するための緊急機能です。')) {
                    await clearAuthCache();
                  }
                }}
                className="text-xs text-gray-500 hover:text-gray-700 underline block w-full"
                title="CloudFormation削除後の認証問題を解決"
              >
                🔧 認証キャッシュクリア（緊急用）
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // 確認コードフォーム
  const ConfirmSignUpForm = () => {
    const [confirmationCode, setConfirmationCode] = useState('');

    const onSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (confirmationData) {
        handleConfirmSignUp(confirmationData.email, confirmationCode);
      }
    };

    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-pink-600 mb-2">確認コード入力</h2>
            <p className="text-gray-600">
              {confirmationData?.email} に送信された確認コードを入力してください
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-700 mb-1">
                確認コード
              </label>
              <input
                type="text"
                id="confirmationCode"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-pink-600 hover:bg-pink-700 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              {isLoading ? '確認中...' : '確認'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setError('');
                  setAuthState('signUp');
                }}
                className="text-pink-600 hover:text-pink-800 text-sm"
              >
                戻る
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // 状態に応じてフォームをレンダリング
  switch (authState) {
    case 'signUp':
      return <CustomSignUpForm onSignUp={handleSignUp} checkFamilyIdAvailability={checkFamilyIdAvailability} />;
    case 'confirmSignUp':
      return <ConfirmSignUpForm />;
    case 'signIn':
    default:
      return <SignInForm />;
  }
};

export default CustomAuthenticator; 